import { useState, useEffect } from 'react';
import { Megaphone } from 'lucide-react';

interface AnnouncementData {
  publicDate: string;
  list: string[];
}

interface AnnouncementModalProps {
  isOpen: boolean;
  onClose: () => void;
  announcement: AnnouncementData | null;
}

export default function AnnouncementModal({
  isOpen,
  onClose,
  announcement,
}: AnnouncementModalProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  // 阻止背景页面滚动
  useEffect(() => {
    if (isOpen) {
      const originalStyle = window.getComputedStyle(document.body).overflow;
      document.body.style.overflow = 'hidden';
      setTimeout(() => setIsVisible(true), 50);
      return () => {
        document.body.style.overflow = originalStyle;
      };
    } else {
      setIsVisible(false);
      setIsClosing(false);
    }
  }, [isOpen]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
      setIsVisible(false);
    }, 300);
  };

  // 检查本地存储，判断是否需要显示公告
  // 只有当 config 接口的 publicDate 与 localStorage 保存的日期不一致时才展示
  useEffect(() => {
    if (isOpen && announcement?.publicDate) {
      const lastSeenDate = localStorage.getItem('lastSeenAnnouncementDate');
      // 如果 localStorage 中保存的日期与当前公告日期一致，则关闭弹窗
      if (lastSeenDate && lastSeenDate === announcement.publicDate) {
        handleClose();
      }
    }
  }, [isOpen, announcement?.publicDate]);

  const handleConfirm = () => {
    if (announcement?.publicDate) {
      localStorage.setItem('lastSeenAnnouncementDate', announcement.publicDate);
    }
    handleClose();
  };

  // 如果没有公告数据，不显示弹窗
  if (
    !isOpen ||
    !announcement ||
    !announcement.list ||
    announcement.list.length === 0
  ) {
    return null;
  }

  return (
    <div
      className={`
        fixed inset-0 z-[70] flex items-center justify-center p-4
        transition-all duration-300 ease-out
        ${isVisible && !isClosing ? 'opacity-100' : 'opacity-0'}
      `}
    >
      {/* 背景遮罩 - 移除点击关闭功能 */}
      <div
        className={`
          absolute inset-0 bg-black/70 backdrop-blur-sm
          transition-opacity duration-300
          ${isVisible && !isClosing ? 'opacity-100' : 'opacity-0'}
        `}
      />

      {/* 弹窗容器 */}
      <div
        className={`
          relative w-full max-w-md
          transform transition-all duration-300 ease-out
          ${isVisible && !isClosing ? 'scale-100 translate-y-0 opacity-100' : 'scale-95 translate-y-4 opacity-0'}
        `}
      >
        {/* 主弹窗 */}
        <div className="relative overflow-hidden rounded-2xl border border-amber-500/20 bg-gradient-to-br from-[#0B0F19] via-[#1a1a1a] to-[#0B0F19] shadow-2xl shadow-amber-500/20">
          {/* 顶部装饰光效 */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-500 via-yellow-500 via-amber-400 via-yellow-500 to-amber-500" />
          <div className="absolute top-0 left-1/4 w-48 h-32 bg-amber-500/10 blur-[60px] rounded-full" />
          <div className="absolute top-0 right-1/4 w-32 h-24 bg-yellow-500/10 blur-[50px] rounded-full" />

          {/* 内容区域 */}
          <div className="relative p-6">
            {/* 头部 */}
            <div className="text-center mb-5">
              {/* 图标 */}
              <div className="relative inline-flex items-center justify-center mb-3">
                <div className="relative w-14 h-14 rounded-full bg-gradient-to-br from-amber-500/20 to-yellow-500/20 border border-amber-500/30 flex items-center justify-center">
                  <Megaphone className="w-7 h-7 text-amber-400" />
                </div>
              </div>

              {/* 主标题 */}
              <h2 className="text-2xl font-bold text-white mb-1">公告栏</h2>
              {/* 发布日期 */}
              <p className="text-sm text-gray-400">
                发布日期：{announcement.publicDate}
              </p>
            </div>

            {/* 公告列表 */}
            <div className="bg-amber-500/5 rounded-xl p-4 border border-amber-500/10 max-h-64 overflow-y-auto">
              <ul className="space-y-3">
                {announcement.list.map((item, index) => (
                  <li
                    key={index}
                    className="flex items-start gap-3 text-sm text-gray-300"
                    style={{ animationDelay: `${index * 0.05}s` }}
                  >
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-gradient-to-r from-amber-500 to-yellow-500 flex items-center justify-center text-xs font-medium text-white mt-0.5">
                      {index + 1}
                    </span>
                    <span className="leading-relaxed pt-0.5">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* 按钮 */}
            <div className="mt-5">
              <button
                onClick={handleConfirm}
                className="group relative w-full py-3 px-6 rounded-xl font-semibold text-white overflow-hidden cursor-pointer transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
              >
                {/* 按钮背景 */}
                <div className="absolute inset-0 bg-gradient-to-r from-amber-600 via-yellow-600 to-amber-600 bg-[length:200%_100%] animate-gradient" />
                {/* 悬停光效 */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                {/* 按钮内容 */}
                <span className="relative z-10">我知道了</span>
              </button>
            </div>
          </div>
        </div>

        {/* 底部装饰 */}
        <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-3/4 h-4 bg-amber-500/20 blur-xl rounded-full" />
      </div>

      {/* CSS 动画 */}
      <style>{`
        @keyframes gradient {
          0%, 100% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
        }
        .animate-gradient {
          animation: gradient 3s ease infinite;
        }
      `}</style>
    </div>
  );
}
